// src/App.js
import React, { Suspense, lazy } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { CompanyInfoProvider } from './context/CompanyInfoContext';
import { WishlistProvider } from './context/WishlistContext';
import { ThemeProvider } from './context/ThemeContext';
import { SeoProvider } from './context/SeoContext';
import { Toaster } from 'react-hot-toast';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HeadTags from './components/HeadTags';
import RouteMeta from './components/RouteMeta';
import DeliveryBanner from './components/DeliveryBanner';
import PrivateRoute from './components/PrivateRoute';

// Route-level code splitting: each page becomes its own chunk that is only
// downloaded when the user navigates to it. This keeps the initial JS payload
// small (critical for first paint, especially on slow connections) instead of
// shipping every page + framer-motion in one big bundle.
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Products = lazy(() => import('./pages/Products'));
const Testimonials = lazy(() => import('./pages/Testimonials'));
const Contact = lazy(() => import('./pages/Contact'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Account = lazy(() => import('./pages/Account'));
const Orders = lazy(() => import('./pages/Orders'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));

// Lightweight, dependency-free fallback shown while a page chunk loads.
const PageFallback = () => (
  <div className="flex justify-center items-center min-h-[60vh]">
    <div className="w-12 h-12 border-4 border-sand-200 dark:border-dark-border border-t-teal-600 rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <HelmetProvider>
    <ThemeProvider>
      <SeoProvider>
      <AuthProvider>
        <CartProvider>
          <CompanyInfoProvider>
            <WishlistProvider>
              <Router>
                <HeadTags />
                <RouteMeta />
                <Toaster position="top-right" />
                <div className="flex flex-col min-h-screen bg-sand-50 dark:bg-dark-bg transition-colors">
                  <DeliveryBanner />
                  <Navbar />
                  <main className="flex-1">
                    <Suspense fallback={<PageFallback />}>
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/products" element={<Products />} />
                        <Route path="/testimonials" element={<Testimonials />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/cart" element={<Cart />} />
                        <Route path="/checkout" element={<Checkout />} />
                        <Route path="/wishlist" element={<Wishlist />} />
                        <Route path="/account" element={<PrivateRoute><Account /></PrivateRoute>} />
                        <Route path="/orders" element={<PrivateRoute><Orders /></PrivateRoute>} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                      </Routes>
                    </Suspense>
                  </main>
                  <Footer />
                </div>
              </Router>
            </WishlistProvider>
          </CompanyInfoProvider>
        </CartProvider>
      </AuthProvider>
      </SeoProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
